import { useEffect, useState } from 'react';
import type { Incident } from '../data/demoData';
import { apiFetch, ApiError } from '../lib/apiClient';

interface SlaPredictionResult {
  ok: true;
  incidentId: string;
  customerId: string;
  generatedAt: string;
  riskScore: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  riskPercentLabel: string;
  reason: string;
  factors: Array<{ label: string; value: string; impact: 'positive' | 'neutral' | 'warning' | 'critical' }>;
  policy: { severity: string; ttaMinutes: number; ttrMinutes: number };
  metrics: {
    ageMinutes: number;
    ageLabel: string;
    ownerAssigned: boolean;
    updateAgeMinutes: number | null;
    updateAgeLabel: string;
    lifecycleUpdateCount: number;
    historicalMeanCloseMinutes: number | null;
    historicalMeanCloseLabel: string;
    openWorkload: number;
    sameOwnerOpenWorkload: number;
    highCriticalOpenWorkload: number;
  };
  drivers: string[];
  recommendedActions: string[];
  diagnostics: string[];
}

interface Props {
  incident: Incident;
  customerId: string;
}

function riskClass(level: string): string {
  const v = level.toLowerCase();
  if (v === 'critical') return 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30';
  if (v === 'high') return 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30';
  if (v === 'medium') return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30';
  return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
}

function factorClass(impact: string): string {
  if (impact === 'critical') return 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300';
  if (impact === 'warning') return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300';
  if (impact === 'positive') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  return 'border-border bg-muted/30 text-muted-foreground';
}

function ProgressBar({ score }: { score: number }) {
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
    </div>
  );
}

function ListBlock({ title, items, empty }: { title: string; items: string[]; empty: string }) {
  return (
    <div className="rounded-xl border bg-background p-3">
      <div className="text-xs font-bold text-foreground">{title}</div>
      {items.length ? (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          {items.map((item, idx) => <li key={`${title}-${idx}`}>{item}</li>)}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

export function IncidentSlaPredictionPanel({ incident, customerId }: Props) {
  const [prediction, setPrediction] = useState<SlaPredictionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadPrediction() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<SlaPredictionResult>(`/incidents/${encodeURIComponent(incident.armIncidentName || incident.id || incident.incidentNumber)}/sla-prediction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, incident }),
      });
      setPrediction(res);
    } catch (e) {
      if (e instanceof ApiError && e.forbidden) setError('Your role is not allowed to view the Smart SLA Breach Predictor. Enable Incidents → View Smart SLA Breach Predictor in Permissions Master.');
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPrediction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id, incident.incidentNumber, customerId]);

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/20">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-amber-900 dark:text-amber-100">Smart SLA Breach Predictor</p>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/80">
            Predicts breach risk before the SLA is missed using age, severity, assignment, update cadence, historical closure baseline and current workload.
          </p>
        </div>
        <button type="button" onClick={() => void loadPrediction()} disabled={loading} className="rounded-lg border border-amber-400/40 bg-background px-3 py-1.5 text-xs font-bold text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:text-amber-200 dark:hover:bg-amber-950">
          {loading ? 'Refreshing…' : 'Refresh Prediction'}
        </button>
      </div>

      {error && <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{error}</div>}

      {!prediction && !error && (
        <div className="mt-3 rounded-lg border bg-card p-4 text-sm text-muted-foreground">{loading ? 'Computing SLA risk…' : 'Prediction will load automatically.'}</div>
      )}

      {prediction && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 lg:grid-cols-[260px_1fr]">
            <div className="rounded-xl border bg-card p-4">
              <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">SLA breach risk</div>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-4xl font-black text-foreground">{prediction.riskScore}</span>
                <span className={`mb-1 rounded-full border px-2.5 py-1 text-xs font-bold ${riskClass(prediction.riskLevel)}`}>{prediction.riskLevel}</span>
              </div>
              <div className="mt-3"><ProgressBar score={prediction.riskScore} /></div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{prediction.reason}</p>
              <p className="mt-2 text-[11px] text-muted-foreground">Policy: TTA {prediction.policy.ttaMinutes}m · TTR {prediction.policy.ttrMinutes}m · {prediction.policy.severity}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {prediction.factors.map((factor) => (
                <div key={factor.label} className={`rounded-xl border p-3 ${factorClass(factor.impact)}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wide opacity-80">{factor.label}</div>
                  <div className="mt-1 text-sm font-bold">{factor.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <ListBlock title="Risk drivers" items={prediction.drivers} empty="No strong breach drivers detected." />
            <ListBlock title="Recommended manager actions" items={prediction.recommendedActions} empty="Continue monitoring." />
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-xl border bg-card p-3"><div className="text-[10px] font-bold uppercase text-muted-foreground">Lifecycle updates</div><div className="mt-1 text-lg font-bold text-foreground">{prediction.metrics.lifecycleUpdateCount}</div></div>
            <div className="rounded-xl border bg-card p-3"><div className="text-[10px] font-bold uppercase text-muted-foreground">Historical close</div><div className="mt-1 text-lg font-bold text-foreground">{prediction.metrics.historicalMeanCloseLabel}</div></div>
            <div className="rounded-xl border bg-card p-3"><div className="text-[10px] font-bold uppercase text-muted-foreground">Open workload</div><div className="mt-1 text-lg font-bold text-foreground">{prediction.metrics.openWorkload}</div></div>
            <div className="rounded-xl border bg-card p-3"><div className="text-[10px] font-bold uppercase text-muted-foreground">High/Critical open</div><div className="mt-1 text-lg font-bold text-foreground">{prediction.metrics.highCriticalOpenWorkload}</div></div>
          </div>

          {prediction.diagnostics.length > 0 && (
            <div className="rounded-xl border border-slate-300 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-950/30 dark:text-slate-300">
              <div className="font-bold">Diagnostics</div>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {prediction.diagnostics.map((d, idx) => <li key={idx}>{d}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
